import { parseDateValue } from "common/dates";

export const DATE_PRESET_KEYS = [
  "LAST_7",
  "LAST_30",
  "LAST_90",
  "MTD",
  "YTD",
  "LAST_WEEK",
  "LAST_MONTH",
  "LAST_QTR",
  "LAST_YEAR",
];

export const DATE_PRESET_LABELS = {
  LAST_7: "Last 7 days",
  LAST_30: "Last 30 days",
  LAST_90: "Last 90 days",
  MTD: "Month to date",
  YTD: "Year to date",
  LAST_WEEK: "Last week",
  LAST_MONTH: "Last month",
  LAST_QTR: "Last quarter",
  LAST_YEAR: "Last year",
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);
const startOfWeekMonday = (d) => {
  const startWeek = new Date(d);
  const day = startWeek.getDay();
  const diff = (day + 6) % 7;
  startWeek.setDate(startWeek.getDate() - diff);
  return startWeek;
};

export function getDatePresetRange(preset) {
  const now = new Date();
  const from = new Date(now);
  let to = null;
  switch (preset) {
    case "LAST_7":
      from.setDate(from.getDate() - 6);
      to = new Date(now);
      break;
    case "LAST_30":
      from.setDate(from.getDate() - 29);
      to = new Date(now);
      break;
    case "LAST_90":
      from.setDate(from.getDate() - 89);
      to = new Date(now);
      break;
    case "MTD":
      from.setTime(startOfMonth(now).getTime());
      to = new Date(now);
      break;
    case "YTD":
      from.setTime(startOfYear(now).getTime());
      to = new Date(now);
      break;
    case "LAST_WEEK": {
      const currentWeekStart = startOfWeekMonday(now);
      const lastWeekStart = new Date(currentWeekStart);
      lastWeekStart.setDate(currentWeekStart.getDate() - 7);
      const lastWeekEnd = new Date(currentWeekStart);
      lastWeekEnd.setDate(currentWeekStart.getDate() - 1);
      from.setTime(lastWeekStart.getTime());
      to = lastWeekEnd;
      break;
    }
    case "LAST_MONTH": {
      const currentMonthStart = startOfMonth(now);
      const lastMonthStart = new Date(
        currentMonthStart.getFullYear(),
        currentMonthStart.getMonth() - 1,
        1
      );
      const lastMonthEnd = new Date(
        currentMonthStart.getFullYear(),
        currentMonthStart.getMonth(),
        0
      );
      from.setTime(lastMonthStart.getTime());
      to = lastMonthEnd;
      break;
    }
    case "LAST_QTR": {
      const q = Math.floor(now.getMonth() / 3);
      const prevQEndMonth = q * 3 - 1;
      const prevQStartMonth = prevQEndMonth - 2;
      const yearForPrevQ =
        prevQEndMonth < 0 ? now.getFullYear() - 1 : now.getFullYear();
      const endMonth = ((prevQEndMonth % 12) + 12) % 12;
      const startMonth = ((prevQStartMonth % 12) + 12) % 12;
      const prevQStart = new Date(yearForPrevQ, startMonth, 1);
      const prevQEnd = new Date(yearForPrevQ, endMonth + 1, 0);
      from.setTime(prevQStart.getTime());
      to = prevQEnd;
      break;
    }
    case "LAST_YEAR": {
      const lastYearStart = new Date(now.getFullYear() - 1, 0, 1);
      const lastYearEnd = new Date(now.getFullYear() - 1, 11, 31);
      from.setTime(lastYearStart.getTime());
      to = lastYearEnd;
      break;
    }
    default:
      break;
  }
  return {
    from,
    to,
  };
}

export function computeDatePresetFromRange(from, to) {
  if (!from) return null;
  const fromMs = from.getTime();
  const toMs = to?.getTime() ?? null;

  const sameDay = (d1Ms, d2) => {
    const d1 = new Date(d1Ms);
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  };

  for (const preset of DATE_PRESET_KEYS) {
    const range = getDatePresetRange(preset);
    const presetToMs = range.to?.getTime() ?? null;

    const fromMatches = sameDay(fromMs, range.from);
    let toMatches = false;
    if (toMs === null && presetToMs === null) {
      toMatches = true;
    } else if (toMs !== null && presetToMs !== null && range.to !== null) {
      toMatches = sameDay(toMs, range.to);
    }
    if (fromMatches && toMatches) {
      return preset;
    }
  }
  return null;
}

export const DEFAULT_DATE_FORMAT = "YYYY-MM-DD";

export function formatDateValue(date, format = DEFAULT_DATE_FORMAT) {
  if (!date) return "";
  const dateObj = parseDateValue(date);

  if (!dateObj) {
    return String(date);
  }

  if (typeof format !== "string") {
    return dateObj.toLocaleDateString();
  }

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return format
    .replace("YYYY", String(year))
    .replace("MM", month)
    .replace("DD", day);
}

// Help text for the "Treat today as" control and its toolbar display. The date
// only moves "today"; it is not a point-in-time view of the data.
export const TREAT_TODAY_AS_HELP_DEFAULT =
  "Moves 'today' for relative dates and filters such as 'last 30 days' or 'this year'. It does not show data as it was on that date.";

// Formats an "as at" (Treat today as) date, a "YYYY-MM-DD" string, in the
// company date format. Returns null for anything that is not such a string so
// the caller renders nothing.
export function formatAsAtDate(value, format) {
  if (typeof value !== "string") return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return null;
  if (!parseDateValue(value)) return null;
  return formatDateValue(value.trim(), format || DEFAULT_DATE_FORMAT);
}

// An example date (30 June of the current year) in the company date format,
// for "Enter a date like ..." hints.
export function exampleDateText(format, now = new Date()) {
  return formatDateValue(
    new Date(now.getFullYear(), 5, 30),
    format || DEFAULT_DATE_FORMAT
  );
}

export function isDateRangeSet(range) {
  if (!range) return false;
  return Boolean(range.from || range.to || range.dynamic);
}

const timeOrNull = (date) => (date ? date.getTime() : null);

const dynamicEqual = (a, b) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.name !== b.name) return false;
  const pa = a.parameters ?? [];
  const pb = b.parameters ?? [];
  return pa.length === pb.length && pa.every((p, i) => p === pb[i]);
};

export function dateRangesEqual(a, b) {
  return (
    timeOrNull(a?.from) === timeOrNull(b?.from) &&
    timeOrNull(a?.to) === timeOrNull(b?.to) &&
    dynamicEqual(a?.dynamic, b?.dynamic)
  );
}

export function findDynamicPresetByName(presets, name) {
  return (presets ?? []).find((preset) => preset.defaultLabel === name) ?? null;
}

export function getDynamicDateDisplayName(presets, name, translateLabels) {
  if (!translateLabels) return name;
  const preset = findDynamicPresetByName(presets, name);
  return preset ? translateLabels(preset.labelKey, { default: name }) : name;
}

// Left padding for an indented date-preset menu entry.
export function datePresetPaddingLeft(theme, indentPx) {
  return `calc(${theme.spacing(2)} + ${indentPx}px)`;
}
